import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { useCircleStore } from '../../stores/circle';
import { api } from '../../lib/api';
import { toast } from '../ui/Toast';
import { isStandalone } from '../../lib/pwa';
import { InstallGate } from './InstallGate';
import type { Circle } from '@nudge/shared';

export function JoinFromInvite() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const ready = useAuthStore((s) => s.ready);
  const register = useAuthStore((s) => s.register);
  const joinCircle = useCircleStore((s) => s.joinCircle);

  const [circlePreview, setCirclePreview] = useState<Circle | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!code) return;
    void api.lookupInvite(code).then((r) => setCirclePreview(r.circle)).catch(() => {
      toast('Invite not found or expired', 'error');
      navigate('/welcome', { replace: true });
    });
  }, [code, navigate]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!code) return;
    setBusy(true);
    try {
      if (!user) {
        const trimmed = name.trim();
        if (!trimmed) { setBusy(false); return; }
        await register(trimmed);
      }
      await joinCircle(code);
      navigate('/app/home', { replace: true });
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not join', 'error');
      setBusy(false);
    }
  }

  if (!ready) return null;
  if (!isStandalone() && code) {
    return <InstallGate intent="join" inviteCode={code} />;
  }

  return (
    <div className="mx-auto flex min-h-screen max-w-screen-sm flex-col items-center justify-center bg-base px-6 text-text-primary">
      <h1 className="text-2xl font-medium">You're invited</h1>
      <p className="mt-2 max-w-sm text-center text-sm text-text-muted">
        {circlePreview ? `Join "${circlePreview.name}"` : 'Loading…'}
      </p>
      <form onSubmit={submit} className="mt-8 w-full max-w-xs">
        {!user && (
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={40}
            placeholder="Your name"
            className="mb-3 w-full rounded-xl border border-border-subtle bg-surface px-4 py-3 text-text-primary outline-none focus:border-sage"
          />
        )}
        <button
          type="submit"
          disabled={busy || !circlePreview || (!user && !name.trim())}
          className="w-full rounded-xl bg-sage py-3 text-base font-medium text-white disabled:opacity-50"
        >
          {busy ? 'Joining…' : 'Join circle'}
        </button>
      </form>
    </div>
  );
}
